import { z } from 'zod';
import { LayoutComponentTypeSchema } from '../content/studio';

/** Guidance deep-link targets (no draft write). */
export const ASK_NAV_TARGETS = [
  'hours',
  'menu',
  'appearance',
  'arrangement',
  'look',
  'utility_bar',
  /** Site tree / Layers — reorder sections on a page (not Arrangement layout). */
  'layers',
  /** Live / Online drawer — publish flow and soft warnings. */
  'publish',
  /** Site → Settings — published versions and Instant rollback. */
  'site_settings',
  /** Operate → Forms / Integrations / Inbox / Reservations. */
  'forms',
  'integrations',
  'inbox',
  'reservations',
] as const;
export type AskNavTarget = (typeof ASK_NAV_TARGETS)[number];

/** Optional workspace selection sent with Ask chat body. */
export const AskSelectionSchema = z
  .object({
    blockId: z.string().uuid(),
    componentType: LayoutComponentTypeSchema,
    fieldKey: z.string().min(1),
    kind: z.enum(['content-field', 'media-field']),
    pageId: z.string().uuid().optional(),
  })
  .nullable()
  .optional();

export type AskSelection = z.infer<typeof AskSelectionSchema>;

/** Active canvas block (layer), even when no field is focused — for Arrangement/Look. */
export const AskActiveBlockSchema = z
  .object({
    blockId: z.string().uuid(),
    componentType: LayoutComponentTypeSchema,
    pageId: z.string().uuid().optional(),
  })
  .nullable()
  .optional();

export type AskActiveBlock = z.infer<typeof AskActiveBlockSchema>;

/**
 * Extra fields on the useChat request body (alongside UIMessage[]).
 * `locale` = site content language for CDL edits.
 * `uiLocale` = Studio workspace chrome language (control labels).
 * `replyLocale` = chat language locked from a typed message. Omitted until then;
 * guidance chips do not set it. Falls back to `uiLocale`.
 */
export const AskAssistRequestExtrasSchema = z.object({
  selection: AskSelectionSchema,
  activeBlock: AskActiveBlockSchema,
  locale: z.string().min(2).max(16).default('de'),
  uiLocale: z.string().min(2).max(16).default('en'),
  replyLocale: z.string().min(2).max(16).optional(),
});

export const AskRouterIntentSchema = z.enum([
  'guidance_hours',
  'guidance_menu',
  'guidance_appearance',
  'guidance_arrangement',
  'guidance_look',
  'guidance_utility_bar',
  'guidance_reorder',
  'guidance_publish',
  'guidance_site_settings',
  'cdl_text',
  'cdl_image',
  'site_import',
  'clarify',
  'general',
]);

export type AskRouterIntent = z.infer<typeof AskRouterIntentSchema>;
